/**
 * UI Store - Zustand store for UI state
 */

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  // AI Chat V1.1 Item 4 — optional inline action (e.g. "Undo" on chat delete).
  // Clicking the action also dismisses the toast.
  action?: ToastAction;
}

interface ModalState {
  id: string;
  data?: unknown;
}

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Modals
  activeModals: ModalState[];
  openModal: (id: string, data?: unknown) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  getModalData: <T>(id: string) => T | undefined;

  // Toasts
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showToastWithAction: (
    message: string,
    type: ToastType,
    action: ToastAction,
    duration?: number
  ) => void;
  dismissToast: (id: string) => void;

  // Session warning
  sessionWarning: {
    visible: boolean;
    timeRemaining: number;
    isSeconds: boolean;
    onContinue?: () => void;
    onLogout?: () => void;
  };
  showSessionWarning: (
    timeRemaining: number,
    isSeconds: boolean,
    onContinue: () => void,
    onLogout: () => void
  ) => void;
  hideSessionWarning: () => void;

  // Command palette
  commandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;

  // Loading overlays
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;

  // Breadcrumbs — set by pages via useBreadcrumbs(), rendered in MainLayout
  breadcrumbs: Array<{ label: string; href?: string }>;
  breadcrumbTrailing: Array<{ text: string; variant: 'neutral' | 'success' | 'warning' | 'error' | 'info' }>;
  setBreadcrumbs: (
    items: Array<{ label: string; href?: string }>,
    trailing?: Array<{ text: string; variant: 'neutral' | 'success' | 'warning' | 'error' | 'info' }>
  ) => void;
  clearBreadcrumbs: () => void;

  // Deal sidebar — set by deal-context pages via useDealSidebar()
  // Drives the 220px left sidebar showing deal-level navigation
  dealSidebarCtx: {
    dealId: number | null;
    termSheetId: number | null;
    active: 'analysis' | 'extraction' | 'negotiations' | 'documents' | null;
    isDemo: boolean;
  } | null;
  setDealSidebar: (ctx: {
    dealId?: number | null;
    termSheetId?: number | null;
    active: 'analysis' | 'extraction' | 'negotiations' | 'documents';
    isDemo?: boolean;
  }) => void;
  clearDealSidebar: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Sidebar
  sidebarCollapsed: false,
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  // Modals
  activeModals: [],
  openModal: (id, data) =>
    set((state) => ({
      activeModals: [...state.activeModals.filter((m) => m.id !== id), { id, data }],
    })),
  closeModal: (id) =>
    set((state) => ({
      activeModals: state.activeModals.filter((m) => m.id !== id),
    })),
  closeAllModals: () => set({ activeModals: [] }),
  getModalData: <T>(id: string) => {
    const modal = get().activeModals.find((m) => m.id === id);
    return modal?.data as T | undefined;
  },

  // Toasts
  toasts: [],
  showToast: (message, type = 'info', duration = 5000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: Toast = { id, message, type, duration };

    set((state) => ({ toasts: [...state.toasts, toast] }));

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        get().dismissToast(id);
      }, duration);
    }
  },
  showToastWithAction: (message, type, action, duration = 5000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: Toast = { id, message, type, duration, action };

    set((state) => ({ toasts: [...state.toasts, toast] }));

    if (duration > 0) {
      setTimeout(() => {
        get().dismissToast(id);
      }, duration);
    }
  },
  dismissToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  // Session warning
  sessionWarning: {
    visible: false,
    timeRemaining: 0,
    isSeconds: false,
  },
  showSessionWarning: (timeRemaining, isSeconds, onContinue, onLogout) =>
    set({
      sessionWarning: {
        visible: true,
        timeRemaining,
        isSeconds,
        onContinue,
        onLogout,
      },
    }),
  hideSessionWarning: () =>
    set((state) => ({
      sessionWarning: { ...state.sessionWarning, visible: false },
    })),

  // Command palette
  commandPaletteOpen: false,
  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),
  toggleCommandPalette: () => set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

  // Global loading
  globalLoading: false,
  setGlobalLoading: (loading) => set({ globalLoading: loading }),

  // Breadcrumbs
  breadcrumbs: [],
  breadcrumbTrailing: [],
  setBreadcrumbs: (items, trailing = []) =>
    set({ breadcrumbs: items, breadcrumbTrailing: trailing }),
  clearBreadcrumbs: () => set({ breadcrumbs: [], breadcrumbTrailing: [] }),

  // Deal sidebar
  dealSidebarCtx: null,
  setDealSidebar: (ctx) =>
    set({
      dealSidebarCtx: {
        dealId: ctx.dealId ?? null,
        termSheetId: ctx.termSheetId ?? null,
        active: ctx.active,
        isDemo: ctx.isDemo ?? false,
      },
    }),
  clearDealSidebar: () => set({ dealSidebarCtx: null }),
}));
